/**
 * Shared machinery for the glyph field: the glyph set, the atlas raster, and
 * the word-target sampler. Used by both the WebGL and 2D-canvas renderers so
 * the fallback is genuinely the same effect at lower fidelity, not a
 * different one.
 *
 * Importing the Devanagari subset here rather than in `src/index.css` is
 * deliberate — this module only ever loads inside a lazy chunk, so the font
 * stays off the critical path and out of the LCP measurement (CLAUDE.md §10).
 */
import '@fontsource/noto-sans-devanagari/devanagari-400.css';

/** Devanagari consonants, vowels and digits. Rendered as texture, never as text. */
export const GLYPHS = [
  'क','ख','ग','घ','ङ','च','छ','ज','झ','ञ','ट','ठ','ड','ढ','ण',
  'त','थ','द','ध','न','प','फ','ब','भ','म','य','र','ल','व','श',
  'ष','स','ह','अ','आ','इ','ई','उ','ऊ','ए','ऐ','ओ','औ','ं','ः',
  '०','१','२','३','४','५','६','७','८','९','ृ','ॉ','ौ','्','़',
] as const;

/** Atlas grid — 8×8 holds all 60 glyphs with room to spare. */
export const ATLAS_COLS = 8;
const CELL = 96;
const FONT_FAMILY = '"Noto Sans Devanagari", sans-serif';

/**
 * Waits for the Devanagari face to actually be usable.
 *
 * `document.fonts.ready` alone is not enough: it resolves once *pending* loads
 * settle, and a face that has never been requested is not pending. The
 * explicit `load()` call requests it first. Getting this wrong bakes fallback
 * glyphs — usually empty boxes — permanently into the atlas.
 */
export async function ensureGlyphFont(): Promise<void> {
  try {
    await document.fonts.load(`64px ${FONT_FAMILY}`, GLYPHS.join(''));
    await document.fonts.ready;
  } catch {
    /* Font loading unavailable — atlas falls back to system glyphs. */
  }
}

/** Renders every glyph once into a square grid canvas, white on transparent. */
export function buildAtlas(): HTMLCanvasElement {
  const rows = Math.ceil(GLYPHS.length / ATLAS_COLS);
  const canvas = document.createElement('canvas');
  canvas.width = ATLAS_COLS * CELL;
  canvas.height = rows * CELL;

  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;

  ctx.fillStyle = '#ffffff';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = `64px ${FONT_FAMILY}`;

  GLYPHS.forEach((glyph, i) => {
    const col = i % ATLAS_COLS;
    const row = Math.floor(i / ATLAS_COLS);
    ctx.fillText(glyph, col * CELL + CELL / 2, row * CELL + CELL / 2);
  });

  return canvas;
}

/**
 * Deterministic PRNG (mulberry32). Used instead of `Math.random()` when
 * seeding the field because generating the layout is done during render, and
 * an impure call there is both a React Compiler violation and a real
 * correctness hazard under concurrent rendering. A fixed seed also makes the
 * field reproducible, so screenshot comparisons across builds are meaningful.
 */
export function makeRandom(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export interface Point2 {
  readonly x: number;
  readonly y: number;
}

/**
 * Samples `count` positions from the opaque pixels of a word, in normalised
 * -0.5..0.5 coordinates with y up.
 *
 * Sampling rasterised pixels rather than deriving glyph outlines is both
 * cheaper and more robust: it needs no font-geometry library and works
 * identically for any string in any face.
 *
 * Returns an empty array if the word rasterises to nothing, so callers can
 * skip the morph rather than collapsing every glyph onto the origin.
 */
export function sampleWord(word: string, count: number, fontFamily: string): Point2[] {
  const W = 480;
  const H = 120;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return [];

  ctx.fillStyle = '#fff';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  // Shrink to fit rather than overflow — long words must not clip.
  let size = 96;
  ctx.font = `600 ${size}px ${fontFamily}`;
  while (ctx.measureText(word).width > W * 0.92 && size > 12) {
    size -= 4;
    ctx.font = `600 ${size}px ${fontFamily}`;
  }
  ctx.fillText(word, W / 2, H / 2);

  const { data } = ctx.getImageData(0, 0, W, H);
  const opaque: Point2[] = [];
  // Step 2px: sub-pixel density would just be discarded by the sampler below.
  for (let y = 0; y < H; y += 2) {
    for (let x = 0; x < W; x += 2) {
      if (data[(y * W + x) * 4 + 3]! > 128) {
        opaque.push({ x: x / W - 0.5, y: 0.5 - y / H });
      }
    }
  }
  if (opaque.length === 0) return [];

  // Even stride rather than random picks: random sampling clumps visibly at
  // this density and leaves holes in the letterforms.
  const out: Point2[] = [];
  for (let i = 0; i < count; i++) {
    out.push(opaque[Math.floor((i / count) * opaque.length)]!);
  }
  return out;
}
